// Importaciones necesarias
import Button from '@mui/material/Button';
import Card from "@mui/material/Card";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import TableSearchField from "components/TableSearchField";
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import MuiFormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArgonBadge from "components/ArgonBadge";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonTypography from "components/ArgonTypography";
import IOSSwitch from "components/IOSSwitch";
import UsuariosSkeleton from "components/SkeletonLoader/UsuariosSkeleton";
import PageBlueHeader from "components/PageBlueHeader";
import PageBlueHeaderSkeleton from "components/PageBlueHeader/PageBlueHeaderSkeleton";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Table from "examples/Tables/Table";
import Cookies from "js-cookie";
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { apiRequest } from "utils/api";
import { useAuth } from "context/AuthContext";
import { useUserData } from "context/UserDataContext";
import { useLanguage } from "context/LanguageContext";

// Función para capitalizar nombres y apellidos (primera letra mayúscula, resto minúscula)
const capitalizeName = (str) => {
  if (!str) return "";
  return str.trim().toLowerCase().split(/\s+/).map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

function Usuarios() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  /** Filtro confirmado por estado (PayFlex Admin) */
  const [statusFilter, setStatusFilter] = useState('todos');
  const [modalFiltro, setModalFiltro] = useState(false);
  const [statusFilterTemp, setStatusFilterTemp] = useState('todos');

  // Estados para el modal de edición
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({
    nombre: '',
    segundoNombre: '',
    apellido: '',
    segundoApellido: '',
    email: '',
    celular: '',
    documento: '',
    rol: '',
    genero: '',
    zafral: '',
    sueldoMensual: '',
    retenciones: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Estados para el modal de confirmación
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Estados para el modal de éxito
  const [successOpen, setSuccessOpen] = useState(false);

  const { user, loading: authLoading } = useAuth();
  const { userData, empresaData } = useUserData();
  const { t } = useLanguage();

  useEffect(() => {
    // Si Firebase está cargando, esperar
    if (authLoading) {
      return;
    }

    // Si no hay usuario autenticado, redirigir al login
    if (!user) {
      navigate("/authentication/sign-in");
      return;
    }

    // Esperar a que userData esté disponible antes de cargar usuarios
    if (!userData || !userData.empresaId) {
      return;
    }

    // Si hay usuario y userData, cargar datos
    fetchUsers();
    obtenerYGuardarUserId();
  }, [navigate, user, authLoading, userData]);

  const abrirModalFiltro = () => {
    setStatusFilterTemp(statusFilter);
    setModalFiltro(true);
  };

  const aplicarFiltroEstado = () => {
    setStatusFilter(statusFilterTemp);
    setModalFiltro(false);
  };

  const obtenerYGuardarUserId = async () => {
    try {
      const response = await apiRequest('/datos-usuario', {
        method: 'GET',
      });

      if (!response.ok) throw new Error('Error al obtener el userId');

      const data = await response.json();
      Cookies.set('userId', data.id); // Guardar el userId en las cookies
    } catch (error) {
      console.error('Error al obtener y guardar userId:', error);
    }
  };

  const registrarAccion = async (accion, detalles = '') => {
    try {
      const userId = Cookies.get('userId');
      if (!userId) {
        console.error('No se pudo obtener el userId de las cookies. Asegúrate de que el usuario esté autenticado.');
        return;
      }

      const bodyData = {
        userId: userId,
        accion,
        detalles: detalles || '',
        origen: 'empresas'
      };

      // Validar que origen no sea null o undefined
      if (!bodyData.origen || bodyData.origen === null) {
        console.error('ERROR: origen es null o undefined, forzando a "empresas"');
        bodyData.origen = 'empresas';
      }


      const jsonBody = JSON.stringify(bodyData);

      const response = await apiRequest('/registrar-accion', {
        method: 'POST',
        body: jsonBody
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.detalles) {
          throw new Error(`Error de validación: ${errorData.detalles.join(', ')}`);
        }
        throw new Error(`Error al registrar la acción: ${errorData.error || 'Error desconocido'}`);
      }

      const responseData = await response.json();
      if (!responseData.origen) {
        console.warn('⚠️ ADVERTENCIA: El backend no devolvió el campo "origen" en la respuesta. Verifica que el backend esté guardando este campo.');
      }
      return responseData;
    } catch (error) {
      console.error('Error al registrar la acción:', error.message);
      throw error; // Propagar el error para manejarlo en el componente que llama a esta función
    }
  };


  const fetchUsers = async () => {
    setLoadingData(true);
    try {
      // Obtener empresaId del contexto del usuario
      const empresaId = userData?.empresaId;

      // Validar que tenemos un empresaId válido antes de hacer la petición
      if (!empresaId) {
        console.error('No se pudo obtener el empresaId del contexto del usuario.');
        setRows([]);
        setLoadingData(false);
        return;
      }

      const response = await apiRequest(`/usuarios-empresa/${empresaId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      const data = await response.json();
      // Asegúrate de que data es un array antes de asignarlo a rows
      if (Array.isArray(data)) {
        setRows(data);
      } else {
        setRows([]); // Si data no es un array, establece rows como un array vacío
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setRows([]); // En caso de error, también establece rows como un array vacío para evitar problemas en el renderizado
    } finally {
      setLoadingData(false);
    }
  };


  const handleToggleActive = async () => {
    if (currentUser) {
      const endpoint = currentUser.activo
        ? `/eliminar-empleado/${currentUser.id}`
        : `/activar-empleado/${currentUser.id}`;

      try {
        const response = await apiRequest(endpoint, {
          method: 'PUT'
        });

        if (response.ok) {
          fetchUsers();
          const accion = currentUser.activo ? 'Desactivar Usuario' : 'Activar Usuario';
          const detalles = `El usuario ${capitalizeName(currentUser.nombre)} ${capitalizeName(currentUser.apellido)} ha sido ${currentUser.activo ? 'desactivado' : 'activado'}`;
          await registrarAccion(accion, detalles);

        } else {
          throw new Error('No se pudo actualizar el estado del empleado');
        }
      } catch (error) {
        console.error('Error al actualizar el estado del empleado:', error);
      }
    }

    handleClose();
  };

  const handleClickOpen = (user) => {
    setCurrentUser(user);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Funciones para el modal de edición
  const handleEditClick = (user) => {
    setEditUser(user);
    setEditForm({
      nombre: user.nombre || '',
      segundoNombre: user.segundoNombre || '',
      apellido: user.apellido || '',
      segundoApellido: user.segundoApellido || '',
      email: user.email || '',
      celular: user.celular || '',
      documento: user.documento || '',
      rol: user.rol || 'empleado', // Siempre empleado
      genero: normalizarGenero(user.genero),
      zafral: normalizarZafral(user.zafral),
      sueldoMensual: user.sueldoMensual ? String(user.sueldoMensual) : '',
      retenciones: user.retenciones || []
    });
    setError('');
    setValidationErrors({});
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditUser(null);
    setEditForm({
      nombre: '',
      segundoNombre: '',
      apellido: '',
      segundoApellido: '',
      email: '',
      celular: '',
      documento: '',
      rol: '',
      genero: '',
      zafral: '',
      sueldoMensual: '',
      retenciones: []
    });
    setError('');
    setValidationErrors({});
  };

  const handleFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));

    // Validación en tiempo real
    const fieldErrors = validateField(field, value);
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      // Si el campo tiene un error en el objeto devuelto, agregarlo
      // Si no tiene error (objeto vacío o sin esa propiedad), eliminarlo
      if (fieldErrors[field]) {
        newErrors[field] = fieldErrors[field];
      } else {
        delete newErrors[field];
      }
      return newErrors;
    });
  };

  // Función para validar campos
  const validateField = (name, value) => {
    const errors = {};

    switch (name) {
      case 'nombre':
        if (!value || value.trim().length === 0) {
          errors.nombre = 'El nombre es obligatorio';
        } else if (value.trim().length < 2) {
          errors.nombre = 'El nombre debe tener al menos 2 caracteres';
        } else if (value.trim().length > 50) {
          errors.nombre = 'El nombre no puede exceder 50 caracteres';
        }
        break;
      case 'apellido':
        if (!value || value.trim().length === 0) {
          errors.apellido = 'El apellido es obligatorio';
        } else if (value.trim().length < 2) {
          errors.apellido = 'El apellido debe tener al menos 2 caracteres';
        } else if (value.trim().length > 50) {
          errors.apellido = 'El apellido no puede exceder 50 caracteres';
        }
        break;
      case 'email':
        if (!value || value.trim().length === 0) {
          errors.email = 'El email es obligatorio';
        } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value.trim())) {
            errors.email = 'El formato del email no es válido';
          }
        }
        break;
      case 'celular':
        // El celular es opcional, pero si se ingresa debe tener el formato correcto
        if (value && value.trim().length > 0) {
          const celularLimpio = value.replace(/\D/g, '');
          if (celularLimpio.length < 8 || celularLimpio.length > 9) {
            errors.celular = 'El celular debe tener entre 8 y 9 dígitos';
          }
        }
        break;
      case 'documento':
        if (!value || value.trim().length === 0) {
          errors.documento = 'El documento es obligatorio';
        } else {
          const documentoLimpio = value.replace(/\D/g, '');
          if (documentoLimpio.length < 7 || documentoLimpio.length > 8) {
            errors.documento = 'El documento debe tener entre 7 y 8 dígitos';
          }
        }
        break;
      case 'rol':
        if (!value || value.trim().length === 0) {
          errors.rol = 'El rol es obligatorio';
        }
        break;
      case 'genero':
        if (!value || value.trim().length === 0) {
          errors.genero = 'El género es obligatorio';
        }
        break;
      case 'zafral':
        if (!value || value.trim().length === 0) {
          errors.zafral = 'El zafral es obligatorio';
        }
        break;
      case 'sueldoMensual':
        // Limpiar el valor: convertir a string, eliminar espacios y caracteres no numéricos
        const valueStr = String(value || '').trim().replace(/\D/g, '');
        if (!valueStr || valueStr === '') {
          errors.sueldoMensual = 'El sueldo es obligatorio';
        } else {
          // Convertir a número
          const sueldo = parseInt(valueStr, 10);
          if (isNaN(sueldo) || sueldo <= 0) {
            errors.sueldoMensual = 'El sueldo debe ser mayor a 0';
          } else if (sueldo > 300000) {
            errors.sueldoMensual = 'El sueldo debe ser menor a 300,000. Para sueldos mayores contacte a soporte';
          } else if (sueldo < 10000) {
            errors.sueldoMensual = 'El sueldo debe ser mayor a 10,000';
          }
        }
        break;
    }

    return errors;
  };

  // Funciones para manejar retenciones
  const handleRetencionChange = (idx, field, value) => {
    const nuevas = editForm.retenciones.map((r, i) => i === idx ? { ...r, [field]: value } : r);
    setEditForm(prev => ({ ...prev, retenciones: nuevas }));
  };

  const handleAgregarRetencion = () => {
    const anioActual = new Date().getFullYear();
    const nuevasRetenciones = [
      ...editForm.retenciones,
      { tipo: '', concepto: '', importe: '', mes: '', anio: anioActual }
    ];
    setEditForm(prev => ({ ...prev, retenciones: nuevasRetenciones }));
  };

  const handleEliminarRetencion = (idx) => {
    setEditForm(prev => ({
      ...prev,
      retenciones: editForm.retenciones.filter((_, i) => i !== idx)
    }));
  };

  // Función para calcular el sueldo líquido
  const calcularSueldoLiquido = () => {
    const sueldoMensual = parseInt(editForm.sueldoMensual) || 0;
    const totalRetenciones = editForm.retenciones.reduce((total, ret) => {
      return total + (parseInt(ret.importe) || 0);
    }, 0);
    return sueldoMensual - totalRetenciones;
  };

  // Datos para las retenciones
  const tiposRetencion = [
    { value: 'otro', label: 'Otro' },
    { value: 'legal', label: 'Legal' },
    { value: 'financiera', label: 'Financiera (cooperativas, préstamos, tarjetas)' },
    { value: 'mutualista', label: 'Mutualista / Seguro médico' },
    { value: 'sindicato', label: 'Sindicato' },
    { value: 'embargo', label: 'Embargo judicial' },
  ];

  const meses = [
    { value: '01', label: t('month.1') },
    { value: '02', label: t('month.2') },
    { value: '03', label: t('month.3') },
    { value: '04', label: t('month.4') },
    { value: '05', label: t('month.5') },
    { value: '06', label: t('month.6') },
    { value: '07', label: t('month.7') },
    { value: '08', label: t('month.8') },
    { value: '09', label: t('month.9') },
    { value: '10', label: t('month.10') },
    { value: '11', label: t('month.11') },
    { value: '12', label: t('month.12') },
  ];

  const anioActual = new Date().getFullYear();
  const anios = [anioActual, anioActual + 1, anioActual + 2];

  // Opciones para los campos de selección
  const opcionesRol = [
    { value: 'admin', label: 'Administrador' },
    { value: 'empleado', label: 'Empleado' },
    { value: 'gerente', label: 'Gerente' },
    { value: 'supervisor', label: 'Supervisor' },
  ];

  // Función para normalizar el género (M/F a valores completos)
  const normalizarGenero = (genero) => {
    if (!genero) return '';
    const generoTrimmed = genero.trim();
    const generoLower = generoTrimmed.toLowerCase();
    // Manejar M/F (mayúsculas o minúsculas)
    if (generoTrimmed === 'M' || generoTrimmed === 'm' || generoLower === 'masculino') return 'masculino';
    if (generoTrimmed === 'F' || generoTrimmed === 'f' || generoLower === 'femenino') return 'femenino';
    // Mantener otros valores si existen
    if (generoLower === 'otro') return 'otro';
    return generoLower;
  };

  // Función para normalizar zafral (boolean a string 'si'/'no')
  const normalizarZafral = (zafral) => {
    if (zafral === true || zafral === 'true' || zafral === 'si' || zafral === 'Si' || zafral === 'SI') return 'si';
    if (zafral === false || zafral === 'false' || zafral === 'no' || zafral === 'No' || zafral === 'NO') return 'no';
    return '';
  };

  const opcionesGenero = [
    { value: 'masculino', label: t('users.gender.male') },
    { value: 'femenino', label: t('users.gender.female') },
    { value: 'otro', label: t('users.gender.other') },
  ];

  const opcionesZafral = [
    { value: 'si', label: t('users.seasonal.yes') },
    { value: 'no', label: t('users.seasonal.no') },
  ];

  // Función para detectar los campos que han cambiado
  const obtenerCamposModificados = () => {
    if (!editUser) return {};

    const cambios = {};

    // Comparar nombre
    if (String(editForm.nombre || '').trim() !== String(editUser.nombre || '').trim()) {
      cambios.nombre = editForm.nombre;
    }

    // Comparar segundoNombre
    if (String(editForm.segundoNombre || '').trim() !== String(editUser.segundoNombre || '').trim()) {
      cambios.segundoNombre = editForm.segundoNombre;
    }

    // Comparar apellido
    if (String(editForm.apellido || '').trim() !== String(editUser.apellido || '').trim()) {
      cambios.apellido = editForm.apellido;
    }

    // Comparar segundoApellido
    if (String(editForm.segundoApellido || '').trim() !== String(editUser.segundoApellido || '').trim()) {
      cambios.segundoApellido = editForm.segundoApellido;
    }

    // Comparar email
    if (String(editForm.email || '').trim() !== String(editUser.email || '').trim()) {
      cambios.email = editForm.email;
    }

    // Comparar celular
    const celularForm = String(editForm.celular || '').trim();
    const celularUser = String(editUser.celular || '').trim();
    if (celularForm !== celularUser) {
      cambios.celular = celularForm || '(vacío)';
    }

    // Comparar documento
    if (String(editForm.documento || '').trim() !== String(editUser.documento || '').trim()) {
      cambios.documento = editForm.documento;
    }

    // Comparar género
    const generoForm = normalizarGenero(editForm.genero);
    const generoUser = normalizarGenero(editUser.genero);
    if (generoForm !== generoUser) {
      cambios.genero = editForm.genero;
    }

    // Comparar zafral
    const zafralForm = normalizarZafral(editForm.zafral);
    const zafralUser = normalizarZafral(editUser.zafral);
    if (zafralForm !== zafralUser) {
      cambios.zafral = editForm.zafral;
    }

    // Comparar sueldo
    const sueldoForm = parseInt(String(editForm.sueldoMensual || '').trim(), 10);
    const sueldoUser = parseInt(String(editUser.sueldoMensual || 0), 10);
    if (sueldoForm !== sueldoUser && !isNaN(sueldoForm)) {
      cambios.sueldoMensual = sueldoForm;
    }

    // Comparar retenciones (simplificado - comparar si hay cambios en cantidad o valores)
    const retencionesForm = editForm.retenciones || [];
    const retencionesUser = editUser.retenciones || [];
    const retencionesCambiaron = JSON.stringify(retencionesForm) !== JSON.stringify(retencionesUser);
    if (retencionesCambiaron) {
      cambios.retenciones = retencionesForm;
    }

    return cambios;
  };

  const handleUpdateUser = () => {
    // Mostrar modal de confirmación
    setConfirmOpen(true);
  };

  const handleConfirmUpdate = async () => {
    if (!editUser) return;

    setLoading(true);
    setError('');
    setConfirmOpen(false);

    try {
      // Obtener solo los campos que han cambiado
      const cambios = obtenerCamposModificados();
      const camposCambiados = Object.keys(cambios);

      if (camposCambiados.length === 0) {
        setError('No se detectaron cambios en los datos del usuario.');
        setLoading(false);
        return;
      }

      // Construir el objeto updateData solo con los campos modificados
      const updateData = {};

      if (cambios.nombre !== undefined) {
        updateData.nombre = String(cambios.nombre).trim();
      }
      if (cambios.segundoNombre !== undefined) {
        updateData.segundoNombre = String(cambios.segundoNombre || '').trim();
      }
      if (cambios.apellido !== undefined) {
        updateData.apellido = String(cambios.apellido).trim();
      }
      if (cambios.segundoApellido !== undefined) {
        updateData.segundoApellido = String(cambios.segundoApellido || '').trim();
      }
      if (cambios.email !== undefined) {
        updateData.email = String(cambios.email).trim();
      }
      if (cambios.celular !== undefined) {
        updateData.celular = cambios.celular === '(vacío)' ? '' : String(cambios.celular).trim();
      }
      if (cambios.documento !== undefined) {
        updateData.documento = String(cambios.documento).trim();
      }
      if (cambios.genero !== undefined) {
        updateData.genero = cambios.genero;
      }
      if (cambios.zafral !== undefined) {
        updateData.zafral = cambios.zafral;
      }
      if (cambios.sueldoMensual !== undefined) {
        updateData.sueldoMensual = cambios.sueldoMensual;
      }
      if (cambios.retenciones !== undefined) {
        // Preparar las retenciones como array de objetos limpio
        updateData.retenciones = cambios.retenciones.map(r => ({
          tipo: r.tipo,
          concepto: r.concepto,
          importe: parseInt(r.importe) || 0,
          mes: r.anio && r.mes ? `${r.anio}-${r.mes}` : '',
        }));
      }

      const response = await apiRequest(`/pre-cambiar-datos-usuario/${editUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar el usuario');
      }

      const result = await response.json();

      // Registrar la acción con solo los campos modificados
      const detallesCampos = camposCambiados.map(campo => {
        const valor = cambios[campo];
        if (campo === 'sueldoMensual') {
          return `${campo}: $${valor.toLocaleString()}`;
        } else if (campo === 'retenciones') {
          return `${campo}: ${valor.length} configuradas`;
        } else if (campo === 'genero') {
          return `${campo}: ${opcionesGenero.find(g => g.value === valor)?.label || valor}`;
        } else if (campo === 'zafral') {
          return `${campo}: ${opcionesZafral.find(z => z.value === valor)?.label || valor}`;
        } else {
          return `${campo}: ${valor}`;
        }
      }).join(', ');

      const detalles = `Solicitud de cambio de datos para usuario ${capitalizeName(editUser.nombre)} ${capitalizeName(editUser.apellido)} - Campos modificados: ${detallesCampos}`;
      await registrarAccion('Solicitar Cambio de Datos', detalles);

      // Mostrar mensaje de éxito
      setError('');
      setSuccessOpen(true);

      // Actualizar la lista de usuarios
      await fetchUsers();

      // Cerrar el modal
      handleEditClose();

    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      setError(error.message || 'Error al actualizar el usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
    setLoading(false);
  };

  const q = searchTerm.toLowerCase();
  const searchedSorted = [...rows]
    .filter((row) =>
      `${row.nombre} ${row.apellido}`.toLowerCase().includes(q) ||
      String(row.documento ?? '').toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const nombreA = `${a.nombre} ${a.apellido}`.toLowerCase();
      const nombreB = `${b.nombre} ${b.apellido}`.toLowerCase();
      return nombreA.localeCompare(nombreB);
    });

  const rowsWithSwitchBase = searchedSorted.map((row) => ({
    ...row,
    nombre: (
      <ArgonTypography variant="button" textTransform="none" fontWeight="regular" color="#000000">
        {`${capitalizeName(row.nombre)} ${capitalizeName(row.apellido)}`}
      </ArgonTypography>
    ),
    documento: `${row.documento}`,
    status: (
      <ArgonBadge
        variant="gradient"
        badgeContent={row.activo ? t('users.active.badge') : t('users.inactive.badge')}
        color={row.activo ? "success" : "secondary"}
        size="xs"
        container
      />
    ),
    activo: (
      <IOSSwitch sx={{ m: 1 }} checked={!!row.activo} onChange={() => handleClickOpen(row)} />
    ),
    editar: (
      <ArgonButton
        variant="outlined"
        color="info"
        size="small"
        onClick={() => handleEditClick(row)}
        sx={{ minWidth: '80px' }}
      >
        {t('common.edit')}
      </ArgonButton>
    ),
  }));

  const matchesStatus = (tableRow) => {
    if (statusFilter === 'todos') return true;
    const checked = tableRow.activo?.props?.checked;
    if (statusFilter === 'activos') return checked === true;
    if (statusFilter === 'inactivos') return checked === false;
    return true;
  };

  const filteredRowsWithSwitch = rowsWithSwitchBase.filter(matchesStatus);

  const tableColumns = useMemo(
    () => [
      { name: 'nombre', align: 'center', label: t('users.table.name') },
      { name: 'documento', align: 'center', label: t('users.table.document') },
      { name: 'status', align: 'center', label: t('users.table.status') },
      { name: 'activo', align: 'center', label: t('users.table.active') },
      { name: 'editar', align: 'center', label: t('users.table.edit') },
    ],
    [t]
  );

  const pageTitle = empresaData?.nombre
    ? t('users.pageTitle', { company: empresaData.nombre, count: filteredRowsWithSwitch.length })
    : t('users.pageTitleFallback', { count: filteredRowsWithSwitch.length });


  return (
    <DashboardLayout>
      {loadingData ? (
        <PageBlueHeaderSkeleton />
      ) : (
        <PageBlueHeader title={pageTitle} subtitle={t('users.pageSubtitle')} />
      )}
      <ArgonBox pt={2} pb={6}>
        <Card>
          <ArgonBox
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            p={3}
            flexWrap="wrap"
            gap={2}
          >
            <ArgonBox display="flex" alignItems="center" gap={2}>
              <TableSearchField
                placeholder={t('users.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <IconButton
                aria-label="filtrar"
                color={statusFilter !== 'todos' ? 'success' : 'primary'}
                onClick={abrirModalFiltro}
              >
                {statusFilter !== 'todos' ? <FilterAltIcon /> : <FilterListIcon />}
              </IconButton>
            </ArgonBox>
          </ArgonBox>
          <ArgonBox sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": {
                borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                  `${borderWidth[1]} solid ${borderColor}`,
              },
            },
            "& thead th": {
              fontSize: '0.9rem',
              fontWeight: 700,
            },
          }}>
            {loadingData ? (
              <UsuariosSkeleton />
            ) : (
              <Table columns={tableColumns} rows={filteredRowsWithSwitch} />
            )}
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />

      <Dialog
        open={modalFiltro}
        onClose={() => setModalFiltro(false)}
        aria-labelledby="filtro-dialog-title"
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogTitle id="filtro-dialog-title">
          {t('users.filter.title')}
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 2, pl: 3.5, pr: 3.5 }}>
          <FormControl component="fieldset" sx={{ pl: 1 }}>
            <RadioGroup
              value={statusFilterTemp}
              onChange={(e) => setStatusFilterTemp(e.target.value)}
            >
              <MuiFormControlLabel value="todos" control={<Radio />} label={t('users.filter.all')} />
              <MuiFormControlLabel value="activos" control={<Radio />} label={t('users.filter.active')} />
              <MuiFormControlLabel value="inactivos" control={<Radio />} label={t('users.filter.inactive')} />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button color="error" onClick={() => setModalFiltro(false)}>
            {t('common.cancel')}
          </Button>
          <Button color="primary" variant="contained" autoFocus onClick={aplicarFiltroEstado}>
            {t('common.apply')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            padding: 0
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{
          fontSize: '1.25rem',
          fontWeight: 'bold',
          padding: '24px 24px 8px 24px',
          textAlign: 'left'
        }}>
          {t('users.toggle.title')}
        </DialogTitle>
        <DialogContent sx={{ padding: '8px 24px 16px 24px' }}>
          <DialogContentText id="alert-dialog-description" sx={{
            color: '#666',
            fontSize: '1rem',
            lineHeight: '1.5'
          }}>
            {t('users.toggle.question', { action: currentUser?.activo ? t('users.toggle.deactivate') : t('users.toggle.activate') })}
          </DialogContentText>
        </DialogContent>
        <div style={{
          padding: '16px 24px 24px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={handleToggleActive}
            autoFocus
            style={{
              backgroundColor: 'rgba(255, 62, 62, 0.9)',
              color: 'white',
              fontSize: '1rem',
              fontWeight: '500',
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              outline: 'none'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 62, 62, 1)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 62, 62, 0.9)'}
          >
            {t('common.confirm')}
          </button>
          <button
            onClick={handleClose}
            style={{
              backgroundColor: 'transparent',
              color: '#1E86F6',
              fontSize: '1rem',
              fontWeight: '500',
              padding: '8px 16px',
              border: 'none',
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            {t('common.cancel')}
          </button>
        </div>
      </Dialog>

      {/* Modal de Edición de Usuario */}
      <Dialog
        open={editOpen}
        onClose={handleEditClose}
        maxWidth="xl"
        fullWidth
        fullScreen
        PaperProps={{
          sx: {
            borderRadius: '0px',
            boxShadow: 'none',
            height: '100vh',
            maxHeight: '100vh',
            margin: 0,
          }
        }}
      >
        <ArgonBox sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <ArgonBox
            sx={{
              p: 2,
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#ffffff'
            }}
          >
            <ArgonTypography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 'bold', textAlign: 'left', color: '#000' }}>
              {capitalizeName(editUser?.nombre)} {capitalizeName(editUser?.apellido)}
            </ArgonTypography>
            <ArgonTypography variant="body2" sx={{ textAlign: 'left', color: '#666', mt: 0.5, fontSize: '0.9rem' }}>
              {t('users.edit.subtitle')}
            </ArgonTypography>
          </ArgonBox>

          {/* Content */}
          <ArgonBox sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {error && (
              <ArgonBox
                sx={{
                  backgroundColor: '#ffebee',
                  color: '#c62828',
                  padding: '10px',
                  borderRadius: '4px',
                  marginBottom: '12px',
                  fontSize: '14px'
                }}
              >
                {error}
              </ArgonBox>
            )}

            <ArgonBox sx={{ p: 1 }}>
              <Grid container spacing={1}>
                {/* Información Personal */}
                <Grid item xs={12}>
                  <ArgonTypography variant="h6" sx={{ mb: 1, mt: 1, color: '#333', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {t('users.edit.personalInfo')}
                  </ArgonTypography>
                </Grid>

                {/* Primera fila: Nombre, Segundo Nombre, Apellido, Segundo Apellido, Documento */}
                <Grid item xs={12} md={4}>
                  <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}>
                    {t('users.edit.firstName')}
                  </ArgonTypography>
                  <TextField
                    fullWidth
                    placeholder={t('users.edit.firstName')}
                    variant="outlined"
                    value={editForm.nombre}
                    onChange={(e) => handleFormChange('nombre', e.target.value)}
                    error={!!validationErrors.nombre}
                    helperText={validationErrors.nombre || " "}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        py: 1.2,
                      },
                      '& .MuiOutlinedInput-input': {
                        paddingLeft: '2px !important',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}>
                    {t('users.edit.secondName')}
                  </ArgonTypography>
                  <TextField
                    fullWidth
                    placeholder={t('users.edit.secondName')}
                    variant="outlined"
                    value={editForm.segundoNombre}
                    onChange={(e) => handleFormChange('segundoNombre', e.target.value)}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        py: 1.2,
                      },
                      '& .MuiOutlinedInput-input': {
                        paddingLeft: '2px !important',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                    {t('users.edit.lastName')}
                  </ArgonTypography>
                  <TextField
                    fullWidth
                    placeholder={t('users.edit.lastName')}
                    variant="outlined"
                    value={editForm.apellido}
                    onChange={(e) => handleFormChange('apellido', e.target.value)}
                    error={!!validationErrors.apellido}
                    helperText={validationErrors.apellido || " "}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        py: 1.2,
                      },
                      '& .MuiOutlinedInput-input': {
                        paddingLeft: '2px !important',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                    {t('users.edit.secondLastName')}
                  </ArgonTypography>
                  <TextField
                    fullWidth
                    placeholder={t('users.edit.secondLastName')}
                    variant="outlined"
                    value={editForm.segundoApellido}
                    onChange={(e) => handleFormChange('segundoApellido', e.target.value)}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        py: 1.2,
                      },
                      '& .MuiOutlinedInput-input': {
                        paddingLeft: '2px !important',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                    {t('users.edit.document')}
                  </ArgonTypography>
                  <TextField
                    fullWidth
                    placeholder="XXXXXXXX"
                    variant="outlined"
                    value={editForm.documento}
                    onChange={(e) => {
                      let newValue = e.target.value.replace(/\D/g, "").slice(0, 8);
                      handleFormChange('documento', newValue);
                    }}
                    error={!!validationErrors.documento}
                    helperText={validationErrors.documento || " "}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: 8,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        py: 1.2,
                      },
                      '& .MuiOutlinedInput-input': {
                        paddingLeft: '2px !important',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                      }
                    }}
                  />
                </Grid>

                {/* Segunda fila: Email */}
                <Grid item xs={12} md={3}>
                  <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                    Email
                  </ArgonTypography>
                  <TextField
                    fullWidth
                    placeholder="ejemplo@gmail.com"
                    variant="outlined"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    error={!!validationErrors.email}
                    helperText={validationErrors.email || " "}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        py: 1.2,
                      },
                      '& .MuiOutlinedInput-input': {
                        paddingLeft: '2px !important',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                      }
                    }}
                  />
                </Grid>

                {/* Tercera fila: Celular, Género, Zafral */}
                <Grid item xs={12} md={3}>
                  <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                    {t('users.edit.phone')}
                  </ArgonTypography>
                  <TextField
                    fullWidth
                    placeholder={t('users.edit.phone')}
                    variant="outlined"
                    value={editForm.celular}
                    onChange={(e) => {
                      let newValue = e.target.value.replace(/\D/g, "").slice(0, 9);
                      handleFormChange('celular', newValue);
                    }}
                    error={!!validationErrors.celular}
                    helperText={validationErrors.celular || " "}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: 9,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        py: 1.2,
                      },
                      '& .MuiOutlinedInput-input': {
                        paddingLeft: '2px !important',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                    {t('users.edit.gender')}
                  </ArgonTypography>
                  <Select
                    fullWidth
                    value={editForm.genero || ''}
                    onChange={e => handleFormChange('genero', e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected === '') {
                        return <span style={{ color: '#999' }}>{t('common.select')}</span>;
                      }
                      const opcion = opcionesGenero.find(opt => opt.value === selected);
                      return opcion ? opcion.label : selected;
                    }}
                    error={!!validationErrors.genero}
                    sx={{
                      '& .MuiSelect-select': {
                        fontSize: '0.9rem',
                        py: 1.2,
                        paddingLeft: '2px !important',
                      },
                    }}
                  >
                    <MenuItem value="" disabled>{t('common.select')}</MenuItem>
                    {opcionesGenero.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  {validationErrors.genero && (
                    <ArgonTypography variant="caption" color="error" sx={{ fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                      {validationErrors.genero}
                    </ArgonTypography>
                  )}
                </Grid>

                <Grid item xs={12} md={3}>
                  <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                    {t('users.edit.seasonal')}
                  </ArgonTypography>
                  <Select
                    fullWidth
                    value={editForm.zafral || ''}
                    onChange={e => handleFormChange('zafral', e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected === '') {
                        return <span style={{ color: '#999' }}>{t('common.select')}</span>;
                      }
                      const opcion = opcionesZafral.find(opt => opt.value === selected);
                      return opcion ? opcion.label : selected;
                    }}
                    error={!!validationErrors.zafral}
                    sx={{
                      '& .MuiSelect-select': {
                        fontSize: '0.9rem',
                        py: 1.2,
                        paddingLeft: '2px !important',
                      },
                    }}
                  >
                    <MenuItem value="" disabled>{t('common.select')}</MenuItem>
                    {opcionesZafral.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  {validationErrors.zafral && (
                    <ArgonTypography variant="caption" color="error" sx={{ fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                      {validationErrors.zafral}
                    </ArgonTypography>
                  )}
                </Grid>

                {/* Sueldo mensual editable */}
                <Grid item xs={12}>
                  <ArgonTypography variant="h6" sx={{ mt: 1.5, mb: 1, color: '#333', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {t('users.edit.monthlySalary')}
                  </ArgonTypography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                    {t('users.edit.netSalary')}
                  </ArgonTypography>
                  <TextField
                    fullWidth
                    placeholder={t('users.edit.enterSalary')}
                    variant="outlined"
                    value={editForm.sueldoMensual}
                    onChange={(e) => {
                      let newValue = e.target.value.replace(/\D/g, "").slice(0, 6);
                      if (newValue === "0") newValue = "";
                      handleFormChange('sueldoMensual', newValue);
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: 6,
                      min: 1,
                      max: 300000
                    }}
                    error={!!validationErrors.sueldoMensual}
                    helperText={validationErrors.sueldoMensual || " "}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        py: 1.2,
                      },
                      '& .MuiOutlinedInput-input': {
                        paddingLeft: '2px !important',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                      }
                    }}
                  />
                </Grid>

                {/* Retenciones */}
                <Grid item xs={12}>
                  <ArgonTypography variant="h6" sx={{ mt: 1.5, mb: 1, color: '#333', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {t('users.edit.withholdings')}
                  </ArgonTypography>
                  {editForm.retenciones.map((ret, idx) => (
                    <Grid container spacing={1} key={idx} alignItems="flex-start" sx={{ mb: 0.5 }}>
                      <Grid item xs={12} md={2}>
                        <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                          {t('users.edit.type')}
                        </ArgonTypography>
                        <Select
                          fullWidth
                          value={ret.tipo}
                          onChange={e => handleRetencionChange(idx, 'tipo', e.target.value)}
                          displayEmpty
                          sx={{
                            '& .MuiSelect-select': {
                              fontSize: '0.9rem',
                              py: 1.2,
                              paddingLeft: '2px !important',
                            },
                          }}
                        >
                          <MenuItem value="" disabled>{t('users.edit.type')} ▼</MenuItem>
                          {tiposRetencion.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                          {t('users.edit.concept')}
                        </ArgonTypography>
                        <TextField
                          fullWidth
                          placeholder={t('users.edit.concept')}
                          value={ret.concepto}
                          onChange={e => handleRetencionChange(idx, 'concepto', e.target.value)}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: '0.9rem',
                              py: 1.2,
                            },
                            '& .MuiOutlinedInput-input': {
                              paddingLeft: '2px !important',
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                          {t('users.edit.amount')}
                        </ArgonTypography>
                        <TextField
                          fullWidth
                          placeholder={t('users.edit.amount')}
                          type="number"
                          value={ret.importe}
                          onChange={e => handleRetencionChange(idx, 'importe', e.target.value.replace(/\D/g, ''))}
                          inputProps={{ min: 0 }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: '0.9rem',
                              py: 1.2,
                            },
                            '& .MuiOutlinedInput-input': {
                              paddingLeft: '2px !important',
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                          {t('users.edit.month')}
                        </ArgonTypography>
                        <Select
                          fullWidth
                          value={ret.mes}
                          onChange={e => handleRetencionChange(idx, 'mes', e.target.value)}
                          displayEmpty
                          sx={{
                            '& .MuiSelect-select': {
                              fontSize: '0.9rem',
                              py: 1.2,
                              paddingLeft: '2px !important',
                            },
                          }}
                        >
                          <MenuItem value="" disabled>{t('users.edit.month')} ▼</MenuItem>
                          {meses.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                          {t('users.edit.year')}
                        </ArgonTypography>
                        <Select
                          fullWidth
                          value={ret.anio}
                          onChange={e => handleRetencionChange(idx, 'anio', e.target.value)}
                          sx={{
                            '& .MuiSelect-select': {
                              fontSize: '0.9rem',
                              py: 1.2,
                              paddingLeft: '2px !important',
                            },
                          }}
                        >
                          {anios.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <ArgonBox display="flex" alignItems="center" justifyContent="center" sx={{ height: '56px', mt: 2.5 }}>
                          <Button
                            onClick={() => handleEliminarRetencion(idx)}
                            sx={{
                              color: '#f5365c',
                              minWidth: 'auto',
                              padding: '4px',
                              '&:hover': {
                                backgroundColor: 'rgba(245, 54, 92, 0.1)',
                              }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: '20px' }} />
                          </Button>
                        </ArgonBox>
                      </Grid>
                    </Grid>
                  ))}
                  <Button
                    color="primary"
                    size="small"
                    onClick={handleAgregarRetencion}
                    sx={{
                      mt: 1,
                      mb: 1,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      backgroundColor: 'rgba(30, 134, 246, 0.11)',
                      color: '#1E86F6',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(26, 115, 210, 0.2)',
                        color: '#1E86F6',
                      },
                    }}
                  >
                    {t('users.edit.addWithholding')}
                  </Button>
                </Grid>

                {/* Resumen de sueldo líquido */}
                <Grid item xs={12}>
                  <ArgonBox
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: '#F8F8F8',
                      textAlign: 'right',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      mb: 1
                    }}
                  >
                    <ArgonTypography variant="body2" color="#666666" sx={{ fontSize: '0.95rem', mb: 0.2 }}>
                      {t('users.edit.liquidSalary')}
                    </ArgonTypography>
                    <ArgonTypography variant="h6" color="#000000" fontWeight="bold" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                      ${calcularSueldoLiquido().toLocaleString()}
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>

                {/* Botones alineados a la derecha */}
                <Grid item xs={12}>
                  <ArgonBox
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 2,
                      mt: 1
                    }}
                  >
                    <ArgonButton
                      onClick={handleEditClose}
                      disabled={loading}
                      sx={{
                        minWidth: '120px',
                        minHeight: 40,
                        py: 0,
                        px: 2,
                        boxSizing: 'border-box',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1.25,
                        fontSize: '0.875rem',
                        color: '#ffffff',
                        backgroundColor: '#f5365c',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: '#c72a49',
                          color: '#ffffff',
                        },
                        '&:disabled': {
                          backgroundColor: '#ccc',
                          color: '#fff'
                        }
                      }}
                    >
                      {t('common.cancel')}
                    </ArgonButton>
                    <ArgonButton
                      onClick={handleUpdateUser}
                      disabled={(() => {
                        // Solo verificar que no haya errores de validación
                        // El usuario puede solicitar cambios de campos individuales sin completar todo el formulario
                        const hayErrores = Object.keys(validationErrors).length > 0;

                        // Si hay un sueldo ingresado, debe ser válido (>= 10000)
                        const sueldoStr = String(editForm.sueldoMensual || '').trim();
                        const sueldoIngresado = sueldoStr && sueldoStr !== '';
                        const sueldoInvalido = sueldoIngresado && (isNaN(parseInt(sueldoStr, 10)) || parseInt(sueldoStr, 10) < 10000);

                        return loading || hayErrores || sueldoInvalido;
                      })()}
                      sx={{
                        minWidth: '150px',
                        minHeight: 40,
                        py: 0,
                        px: 2,
                        boxSizing: 'border-box',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1.25,
                        fontSize: '0.875rem',
                        color: '#ffffff',
                        WebkitTextFillColor: '#ffffff',
                        backgroundColor: '#1E86F6',
                        fontWeight: 'bold',
                        opacity: 1,
                        '&:hover': {
                          backgroundColor: '#1976d2',
                          color: '#ffffff',
                          WebkitTextFillColor: '#ffffff',
                          opacity: 1,
                        },
                        '&:focus': {
                          color: '#ffffff',
                          WebkitTextFillColor: '#ffffff',
                        },
                        '&:focus-visible': {
                          color: '#ffffff',
                          WebkitTextFillColor: '#ffffff',
                          backgroundColor: '#1976d2',
                        },
                        '&.Mui-disabled': {
                          opacity: '1 !important',
                          backgroundColor: '#d1d5db !important',
                          color: 'rgba(15, 23, 42, 0.72) !important',
                          WebkitTextFillColor: 'rgba(15, 23, 42, 0.72)',
                        },
                      }}
                    >
                      {loading ? t('users.edit.saving') : t('users.edit.requestChange')}
                    </ArgonButton>
                  </ArgonBox>
                </Grid>

              </Grid>
            </ArgonBox>
          </ArgonBox>
        </ArgonBox>
      </Dialog>

      {/* Modal de Confirmación para Solicitar Cambios */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelConfirm}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            padding: 0,
            maxWidth: '500px'
          }
        }}
      >
        <DialogTitle id="confirm-dialog-title" sx={{
          fontSize: '1.25rem',
          fontWeight: 'bold',
          padding: '24px 24px 8px 24px',
          textAlign: 'center'
        }}>
          {t('users.confirm.title')}
        </DialogTitle>
        <DialogContent sx={{ padding: '8px 24px 16px 24px' }}>
          <DialogContentText id="confirm-dialog-description" sx={{
            color: '#666',
            fontSize: '1rem',
            lineHeight: '1.5',
            textAlign: 'center'
          }}>
            {(() => {
              const cambios = obtenerCamposModificados();
              const camposCambiados = Object.keys(cambios);

              if (camposCambiados.length === 0) {
                return (
                  <>
                    {t('users.confirm.noChanges')}
                    <br /><br />
                    {t('users.confirm.pleaseModify')}
                  </>
                );
              }

              return (
                <>
                  {t('users.confirm.question')} <strong>{editUser?.nombre} {editUser?.apellido}</strong>?
                  <br /><br />
                  <strong>{t('users.confirm.dataToModify')}</strong>
                  <br />
                  {cambios.nombre && (
                    <>
                      <strong>Nombre:</strong> {cambios.nombre}
                      <br />
                    </>
                  )}
                  {cambios.segundoNombre !== undefined && (
                    <>
                      <strong>Segundo Nombre:</strong> {cambios.segundoNombre || '(vacío)'}
                      <br />
                    </>
                  )}
                  {cambios.apellido && (
                    <>
                      <strong>Apellido:</strong> {cambios.apellido}
                      <br />
                    </>
                  )}
                  {cambios.segundoApellido !== undefined && (
                    <>
                      <strong>Segundo Apellido:</strong> {cambios.segundoApellido || '(vacío)'}
                      <br />
                    </>
                  )}
                  {cambios.email && (
                    <>
                      <strong>Email:</strong> {cambios.email}
                      <br />
                    </>
                  )}
                  {cambios.celular !== undefined && (
                    <>
                      <strong>Celular:</strong> {cambios.celular}
                      <br />
                    </>
                  )}
                  {cambios.documento && (
                    <>
                      <strong>Documento:</strong> {cambios.documento}
                      <br />
                    </>
                  )}
                  {cambios.genero && (
                    <>
                      <strong>Género:</strong> {opcionesGenero.find(g => g.value === cambios.genero)?.label || cambios.genero}
                      <br />
                    </>
                  )}
                  {cambios.zafral && (
                    <>
                      <strong>Zafral:</strong> {opcionesZafral.find(z => z.value === cambios.zafral)?.label || cambios.zafral}
                      <br />
                    </>
                  )}
                  {cambios.sueldoMensual !== undefined && (
                    <>
                      <strong>Nuevo sueldo:</strong> ${cambios.sueldoMensual.toLocaleString()}
                      <br />
                      <strong>Sueldo líquido:</strong> ${calcularSueldoLiquido().toLocaleString()}
                      <br />
                    </>
                  )}
                  {cambios.retenciones && (
                    <>
                      <strong>Retenciones:</strong> {cambios.retenciones.length} configuradas
                      <br />
                    </>
                  )}
                  <br />
                  {t('users.confirm.pendingApproval')}
                </>
              );
            })()}
          </DialogContentText>
        </DialogContent>
        <div style={{
          padding: '16px 24px 24px 24px',
          display: 'flex',
          justifyContent: 'center',
          gap: '12px'
        }}>
          <button
            onClick={handleCancelConfirm}
            style={{
              backgroundColor: 'transparent',
              color: '#666',
              fontSize: '1rem',
              fontWeight: '500',
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid #ddd',
              cursor: 'pointer',
              outline: 'none'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleConfirmUpdate}
            disabled={Object.keys(obtenerCamposModificados()).length === 0}
            style={{
              backgroundColor: Object.keys(obtenerCamposModificados()).length === 0 ? '#ccc' : '#1E86F6',
              color: 'white',
              fontSize: '1rem',
              fontWeight: '500',
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              cursor: Object.keys(obtenerCamposModificados()).length === 0 ? 'not-allowed' : 'pointer',
              outline: 'none'
            }}
            onMouseEnter={(e) => {
              if (Object.keys(obtenerCamposModificados()).length > 0) {
                e.target.style.backgroundColor = '#1976d2';
              }
            }}
            onMouseLeave={(e) => {
              if (Object.keys(obtenerCamposModificados()).length > 0) {
                e.target.style.backgroundColor = '#1E86F6';
              }
            }}
          >
            {t('users.confirm.btn')}
          </button>
        </div>
      </Dialog>

      {/* Modal de Éxito */}
      <Dialog
        open={successOpen}
        onClose={() => {
          setSuccessOpen(false);
          handleEditClose();
        }}
        aria-labelledby="success-dialog-title"
        aria-describedby="success-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            padding: 0,
            maxWidth: '500px'
          }
        }}
      >
        <DialogTitle id="success-dialog-title" sx={{ color: '#4caf50', padding: '24px 24px 8px 24px' }}>
          <ArgonBox display="flex" alignItems="center" justifyContent="center" gap={1}>
            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '2rem' }} />
            <span style={{ fontWeight: 'bold', fontSize: '1.25rem' }}>{t('users.success.title')}</span>
          </ArgonBox>
        </DialogTitle>
        <DialogContent sx={{ padding: '8px 24px 16px 24px' }}>
          <DialogContentText id="success-dialog-description" sx={{
            color: '#666',
            fontSize: '1rem',
            lineHeight: '1.5',
            textAlign: 'center'
          }}>
            <strong>{t('users.success.pending')}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px 24px 24px', justifyContent: 'center' }}>
          <Button
            onClick={() => {
              setSuccessOpen(false);
              handleEditClose();
            }}
            variant="contained"
            autoFocus
            sx={{
              backgroundColor: '#1E86F6',
              color: 'white',
              '&:hover': {
                backgroundColor: '#1976d2',
              },
              padding: '10px 20px',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            {t('common.accept')}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Usuarios;